import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getUser, blockUser, unblockUser, applyDiscount, removeDiscount, setUserRole } from '../../api/users'
import { adminCreateOrder, calcPreviewByPrice } from '../../api/orders'
import { getSettings } from '../../api/settings'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'
import { useTelegram } from '../../hooks/useTelegram'
import { formatDate } from '../../utils/format'

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user: me, isSuperadmin } = useAuth()
  const { tg } = useTelegram()
  const lang = me?.language ?? 'ru'
  const tr = useT(lang)
  const qc = useQueryClient()

  const [blockReason, setBlockReason] = useState('')
  const [discountPct, setDiscountPct] = useState('')
  const [discountReason, setDiscountReason] = useState('')
  const [showBlock, setShowBlock] = useState(false)
  const [showDiscount, setShowDiscount] = useState(false)
  const [showOrder, setShowOrder] = useState(false)
  const [orderProductName, setOrderProductName] = useState('')
  const [orderPrice, setOrderPrice] = useState('')
  const [orderDown, setOrderDown] = useState('0')
  const [orderMonths, setOrderMonths] = useState(12)
  const [orderComment, setOrderComment] = useState('')

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => getUser(Number(id)),
    enabled: !!id,
  })

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, enabled: showOrder })
  const maxMonths = settings?.max_months ?? 24
  const orderPriceNum = parseFloat(orderPrice) || 0
  const orderDownNum = parseFloat(orderDown) || 0
  const canCalcOrder = orderPriceNum > 0 && orderMonths >= 1 && orderMonths <= maxMonths && orderProductName.trim().length >= 2

  const { data: orderPreview } = useQuery({
    queryKey: ['calc-admin-order', orderPriceNum, orderDownNum, orderMonths],
    queryFn: () => calcPreviewByPrice({
      product_price: orderPriceNum, down_payment: orderDownNum, months: orderMonths,
      discount_pct: user?.personal_discount ?? 0,
    }),
    enabled: showOrder && canCalcOrder,
    placeholderData: (prev) => prev,
  })

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  const invalidate = () => qc.invalidateQueries({ queryKey: ['user', id] })

  const blockMut = useMutation({
    mutationFn: () => blockUser(Number(id), blockReason),
    onSuccess: () => { toast.success(tr('blockUserSuccess')); setShowBlock(false); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const unblockMut = useMutation({
    mutationFn: () => unblockUser(Number(id)),
    onSuccess: () => { toast.success(tr('unblockUserSuccess')); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const discountMut = useMutation({
    mutationFn: () => applyDiscount(Number(id), Number(discountPct), discountReason || undefined),
    onSuccess: () => { toast.success(tr('discountApplied')); setShowDiscount(false); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const removeDiscountMut = useMutation({
    mutationFn: () => removeDiscount(Number(id)),
    onSuccess: () => { toast.success(tr('discountRemoved')); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const roleMut = useMutation({
    mutationFn: (role: 'admin' | 'client') => setUserRole(Number(id), role),
    onSuccess: () => { toast.success(tr('roleChanged')); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const createOrderMut = useMutation({
    mutationFn: () => adminCreateOrder({
      user_id: Number(id),
      product_name: orderProductName.trim(),
      product_price: orderPriceNum,
      down_payment: orderDownNum,
      months: orderMonths,
      comment: orderComment.trim() || undefined,
    }),
    onSuccess: (order) => {
      toast.success(tr('orderSentToClient'))
      setShowOrder(false)
      setOrderProductName(''); setOrderPrice(''); setOrderDown('0'); setOrderMonths(12); setOrderComment('')
      navigate(`/admin/orders/${order.id}`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  if (isLoading) return <Spinner className="h-screen" />
  if (!user) return null

  return (
    <Layout hideNav>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        {tr('clients')}
      </button>

      {/* User info */}
      <div className="card mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-tg-hint/20 flex items-center justify-center text-xl font-semibold text-tg-hint">
            {user.full_name[0]}
          </div>
          <div>
            <p className="font-semibold text-tg-text">{user.full_name}</p>
            <p className="text-sm text-tg-hint">{user.phone}</p>
          </div>
        </div>
        <div className="mt-3 space-y-1.5 text-sm">
          <Row label={tr('telegramId')} value={user.telegram_id} />
          <Row label={tr('role')} value={user.role} />
          <Row label={tr('verificationLabel')} value={user.is_verified ? `✅ ${tr('verified')}` : `⏳ ${tr('notVerified')}`} />
          {user.passport_verified && !user.is_verified && (
            <Row label={tr('passportLabel')} value={tr('passportUploaded')} />
          )}
          <Row label={tr('status')} value={user.is_active ? tr('activeStatus') : tr('blockedStatus')} />
          {user.personal_discount > 0 && <Row label={tr('discount')} value={`${user.personal_discount}%`} />}
          <Row label={tr('registrationDate')} value={formatDate(user.created_at)} />
          {user.created_by_id && <Row label={tr('createdBy')} value={`ID ${user.created_by_id}`} />}
          {user.last_seen_at && <Row label={tr('lastVisit')} value={formatDate(user.last_seen_at)} />}
        </div>
      </div>

      {/* Block/Unblock */}
      {!showBlock && !showDiscount && !showOrder && (
        <div className="flex gap-3 mb-3">
          {user.is_active ? (
            <button onClick={() => setShowBlock(true)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}>
              {tr('block')}
            </button>
          ) : (
            <button onClick={() => unblockMut.mutate()} disabled={unblockMut.isPending} className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ background: 'rgba(111,184,154,0.10)', color: '#6FB89A' }}>
              {tr('unblock')}
            </button>
          )}
          <button onClick={() => setShowDiscount(true)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: '#1A1A1E', color: '#F5F4F0' }}>
            {tr('discount')}
          </button>
        </div>
      )}

      {/* Open an application on behalf of the client */}
      {user.role === 'client' && !showBlock && !showDiscount && !showOrder && (
        <div className="mb-3">
          <button onClick={() => setShowOrder(true)} className="w-full py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'rgba(200,155,76,0.14)', color: '#C89B4C' }}>
            {tr('openOrderForClient')}
          </button>
        </div>
      )}

      {/* Order form */}
      {showOrder && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-2">{tr('openOrderForClient')}</h3>
          <input
            className="input mb-2"
            placeholder={tr('enterProductName')}
            value={orderProductName}
            onChange={(e) => setOrderProductName(e.target.value)}
          />
          <input
            type="number" className="input mb-2"
            placeholder={tr('enterPrice')}
            value={orderPrice}
            onChange={(e) => setOrderPrice(e.target.value)}
          />
          <input
            type="number" className="input mb-2"
            placeholder={tr('downPayment')}
            value={orderDown}
            onChange={(e) => setOrderDown(e.target.value)}
          />
          <input
            type="number" inputMode="numeric" className="input mb-2"
            min={1} max={maxMonths} step={1} list="term-presets"
            placeholder={`${tr('monthsShort')}: 1–${maxMonths}`}
            value={orderMonths || ''}
            onChange={(e) => setOrderMonths(parseInt(e.target.value, 10) || 0)}
          />
          <datalist id="term-presets">
            {[1, 3, 6, 9, 12, 18, 24].filter((m) => m <= maxMonths).map((m) => <option key={m} value={m} />)}
          </datalist>
          <input
            className="input mb-3"
            placeholder={tr('enterComment')}
            value={orderComment}
            onChange={(e) => setOrderComment(e.target.value)}
          />
          {orderPreview && (
            <div className="text-sm text-tg-hint mb-3">
              {tr('monthlyPayment')}: <span className="text-tg-text font-semibold">{orderPreview.monthly_payment.toFixed(2)} $</span>
              {' · '}{tr('rate')} {orderPreview.interest_rate}%
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => createOrderMut.mutate()}
              disabled={!canCalcOrder || !orderPreview || createOrderMut.isPending}
              className="btn-primary flex-1 disabled:opacity-40"
            >
              {tr('openOrderForClient')}
            </button>
            <button onClick={() => setShowOrder(false)} className="btn-secondary flex-1">{tr('cancel')}</button>
          </div>
        </div>
      )}

      {/* Block form */}
      {showBlock && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-2">{tr('block')}</h3>
          <input
            className="input mb-3"
            placeholder={tr('reason')}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
          />
          <div className="flex gap-3">
            <button onClick={() => blockMut.mutate()} disabled={!blockReason || blockMut.isPending} className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}>
              {tr('block')}
            </button>
            <button onClick={() => setShowBlock(false)} className="btn-secondary flex-1">{tr('cancel')}</button>
          </div>
        </div>
      )}

      {/* Discount form */}
      {showDiscount && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-2">{tr('discountPct')}</h3>
          <input type="number" className="input mb-2" placeholder="5" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} />
          <input className="input mb-3" placeholder={tr('reason')} value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} />
          <div className="flex gap-3">
            <button onClick={() => discountMut.mutate()} disabled={!discountPct || discountMut.isPending} className="btn-primary flex-1 disabled:opacity-40">
              {tr('apply')}
            </button>
            {user.personal_discount > 0 && (
              <button onClick={() => removeDiscountMut.mutate()} disabled={removeDiscountMut.isPending} className="flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}>
                {tr('delete')}
              </button>
            )}
            <button onClick={() => setShowDiscount(false)} className="btn-secondary flex-1">{tr('cancel')}</button>
          </div>
        </div>
      )}

      {/* Role management (superadmin only) */}
      {isSuperadmin && user.role !== 'superadmin' && (
        <div className="card">
          <p className="section-title">{tr('role')}</p>
          <div className="flex gap-3 mt-2">
            {(['client', 'admin'] as const).map((r) => (
              <button
                key={r}
                onClick={() => roleMut.mutate(r)}
                disabled={user.role === r || roleMut.isPending}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-40"
                style={user.role === r
                  ? { background: '#F97316', color: '#0A0A0B' }
                  : { background: '#1A1A1E', color: '#8A8A96' }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      )}
    </Layout>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-tg-hint">{label}</span>
      <span className="text-tg-text">{value}</span>
    </div>
  )
}
